import { useState } from "react"
import { Head, router } from "@inertiajs/react"
import { AdminShell } from "@/components/AdminShell"
import { EntityCombobox } from "@/components/EntityCombobox"
import { Button } from "@/components/ui/button"
import { AlertTriangle, ArrowRight } from "lucide-react"

type CountMap = Record<string, number>

type Preview = {
  canonical: { id: number; name: string }
  duplicate: { id: number; name: string }
  counts:    CountMap
}

type Props = {
  type:         string | null
  canonical_id: number | null
  duplicate_id: number | null
  preview:      Preview | null
}

const TYPES = ["Band", "Person", "Venue", "RecordLabel"] as const
type MergeType = typeof TYPES[number]

const TYPE_LABELS: Record<MergeType, string> = {
  Band:        "Band",
  Person:      "Person / Artist",
  Venue:       "Venue",
  RecordLabel: "Record Label",
}

const COUNT_LABELS: Record<string, string> = {
  gigs:              "Gigs",
  recordings:        "Recordings",
  members:           "Members",
  credits:           "Credits",
  image_attachments: "Image attachments",
}

function CountSummary({ counts }: { counts: CountMap }) {
  const nonZero = Object.entries(counts).filter(([, v]) => v > 0)
  if (nonZero.length === 0) {
    return <p className="text-sm text-stone-400 dark:text-stone-500 italic">No associated records to re-point.</p>
  }
  return (
    <ul className="space-y-1">
      {nonZero.map(([key, count]) => (
        <li key={key} className="flex items-center gap-2 text-sm">
          <span className="w-4 h-4 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-300 text-xs font-mono flex items-center justify-center shrink-0">
            {count > 9 ? "+" : count}
          </span>
          <span className="text-stone-700 dark:text-stone-300">
            {count} {COUNT_LABELS[key] ?? key} will be re-pointed to the canonical record
          </span>
        </li>
      ))}
    </ul>
  )
}

export default function MergesNew({ type, canonical_id, duplicate_id, preview }: Props) {
  const [selectedType,      setSelectedType]      = useState<string>(type ?? "")
  const [selectedCanonical, setSelectedCanonical] = useState<{ id: string; name: string } | null>(
    canonical_id ? { id: String(canonical_id), name: preview?.canonical.name ?? "" } : null
  )
  const [selectedDuplicate, setSelectedDuplicate] = useState<{ id: string; name: string } | null>(
    duplicate_id ? { id: String(duplicate_id), name: preview?.duplicate.name ?? "" } : null
  )

  function loadPreview() {
    if (!selectedType || !selectedCanonical || !selectedDuplicate) return
    router.get("/admin/merges/new", {
      type:         selectedType,
      canonical_id: selectedCanonical.id,
      duplicate_id: selectedDuplicate.id,
    }, { preserveState: false })
  }

  function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    if (!preview) return
    router.post("/admin/merges", {
      type:         type,
      canonical_id: preview.canonical.id,
      duplicate_id: preview.duplicate.id,
    })
  }

  const readyToPreview = selectedType && selectedCanonical && selectedDuplicate &&
    selectedCanonical.id !== selectedDuplicate.id

  return (
    <>
      <Head title="Merge duplicate records — Admin">
        <meta name="robots" content="noindex" />
      </Head>
      <AdminShell>
        <h1 className="mb-2">Merge duplicate records</h1>
        <p className="text-sm text-stone-500 dark:text-stone-400 mb-8">
          All associations, images, and slug history are transferred from the duplicate to the canonical record. The duplicate is then permanently deleted.
        </p>

        {/* Step 1 — select records */}
        <div className="bg-surface border border-hairline rounded-xl p-6 space-y-5 mb-8">
          <h2 className="text-sm font-semibold text-stone-700 dark:text-stone-300 uppercase tracking-wide">
            Step 1 — Select records
          </h2>

          <div>
            <label className="block text-sm font-medium text-ink-body mb-1">Record type</label>
            <select
              value={selectedType}
              onChange={(e) => { setSelectedType(e.target.value); setSelectedCanonical(null); setSelectedDuplicate(null) }}
              className="w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body focus:outline-none focus:ring-2 focus:ring-primary"
            >
              <option value="">Choose a type…</option>
              {TYPES.map((t) => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
            </select>
          </div>

          {selectedType && (
            <>
              <div>
                <label className="block text-sm font-medium text-ink-body mb-1">
                  Keep this one <span className="text-stone-400">(canonical)</span>
                </label>
                <EntityCombobox
                  key={`canonical-${selectedType}`}
                  type={selectedType}
                  initialId={selectedCanonical?.id ?? ""}
                  initialName={selectedCanonical?.name ?? ""}
                  placeholder={`Search for a ${TYPE_LABELS[selectedType as MergeType]}…`}
                  inputClass="w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body"
                  onSelect={(id, name) => setSelectedCanonical(id ? { id, name } : null)}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-ink-body mb-1">
                  Delete this one <span className="text-stone-400">(duplicate)</span>
                </label>
                <EntityCombobox
                  key={`duplicate-${selectedType}`}
                  type={selectedType}
                  initialId={selectedDuplicate?.id ?? ""}
                  initialName={selectedDuplicate?.name ?? ""}
                  placeholder={`Search for a ${TYPE_LABELS[selectedType as MergeType]}…`}
                  inputClass="w-full rounded-md border border-hairline bg-page px-3 py-2 text-sm text-ink-body"
                  onSelect={(id, name) => setSelectedDuplicate(id ? { id, name } : null)}
                />
              </div>

              <Button
                type="button"
                variant="secondary"
                disabled={!readyToPreview}
                onClick={loadPreview}
              >
                Preview merge
              </Button>
            </>
          )}
        </div>

        {/* Step 2 — preview + confirm */}
        {preview && (
          <form onSubmit={handleSubmit}>
            <div className="bg-surface border border-amber-200 dark:border-amber-800 rounded-xl p-6 space-y-5">
              <h2 className="text-sm font-semibold text-stone-700 dark:text-stone-300 uppercase tracking-wide">
                Step 2 — Confirm merge
              </h2>

              <div className="flex items-center gap-3 text-sm">
                <span className="font-medium text-stone-800 dark:text-stone-100">{preview.duplicate.name}</span>
                <ArrowRight className="w-4 h-4 text-stone-400 shrink-0" />
                <span className="font-medium text-stone-800 dark:text-stone-100">{preview.canonical.name}</span>
              </div>

              <CountSummary counts={preview.counts} />

              <div className="flex items-start gap-2 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-200 dark:border-amber-800 px-4 py-3">
                <AlertTriangle className="w-4 h-4 text-amber-600 dark:text-amber-400 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800 dark:text-amber-300">
                  <strong>"{preview.duplicate.name}"</strong> will be permanently deleted. This cannot be undone.
                </p>
              </div>

              <Button type="submit" className="bg-red-600 hover:bg-red-700 text-white border-red-600">
                Merge and delete duplicate
              </Button>
            </div>
          </form>
        )}
      </AdminShell>
    </>
  )
}
